import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { useSelectorActiveChain } from 'components/hooks';
import { Chains } from 'config/constants';

export type Colors = keyof typeof colors;

export type ButtonCssProps = {
  $colorName: Colors;
  $isInline?: boolean;
  $isBordered: boolean;
  $widePadding: boolean;
  $minimalPadding: boolean;
  $fontNotBold: boolean;
  $activeChain: Chains;
};

const colors = {
  active: css`
    --button-bg: var(--main);
    --button-bg-hover: var(--primary2);
    --button-text: var(--white);
    --button-text-hover: var(--white);
    &[disabled] {
      --button-bg: var(--mainDark);
      --button-text: var(--main);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  inactive: css`
    --button-bg: var(--primary4);
    --button-bg-hover: var(--primary4);
    --button-text: var(--black);
    --button-text-hover: var(--black);
    &[disabled] {
      opacity: 0.5;
    }
    &:focus:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  fancyGradient: css`
    background: linear-gradient(
      270deg,
      #fd0598 -9.74%,
      #8f00ff 102.25%,
      #8f00ff 102.26%
    );
    color: var(--white);

    &[disabled] {
      opacity: 0.7;
    }
    &:hover:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  success: css`
    --button-bg: var(--green4);
    --button-bg-hover: var(--green3);
    --button-text: var(--white);
    --button-text-hover: var(--button-text);
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  successWithDarkText: css`
    --button-bg: var(--green4);
    --button-bg-hover: var(--green5);
    --button-text: var(--black);
    --button-text-hover: var(--black);
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  gray: css`
    --button-bg: var(--separator);
    --button-bg-hover: var(--primary2);
    --button-text: var(--black);
    --button-text-hover: var(--black);
    font-weight: normal;
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  black: css`
    --button-bg: var(--black);
    --button-bg-hover: var(--lightBlack);
    --button-text: var(--white);
    --button-text-hover: var(--white);
    font-weight: normal;
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  red: css`
    --button-bg: var(--red3);
    --button-bg-hover: var(--red3b);
    --button-text: var(--black);
    --button-text-hover: var(--black);
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  blue: css`
    --button-bg: var(--blue2);
    --button-bg-hover: var(--blue3);
    --button-text: var(--white);
    --button-text-hover: var(--white);
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
  yellow2: css`
    --button-bg: var(--yellow2);
    --button-bg-hover: var(--yellow3);
    --button-text: var(--white);
    --button-text-hover: var(--white);
    &[disabled] {
      opacity: 0.5;
    }
    &:focus-visible:not([disabled]) {
      filter: brightness(110%);
    }
    &:active:not([disabled]) {
      filter: brightness(90%);
    }
  `,
};

const borderedStyle = css<ButtonCssProps>`
  /* Increase specificity of bordering styles */
  &&[disabled] {
    --button-bg: ${({ $colorName }) =>
      $colorName === 'success' ? 'var(--success)' : 'var(--main)'};
    opacity: 0.5;
  }
  && {
    --button-text-hover: var(--white);
    --button-text: ${({ $colorName }) =>
      $colorName === 'red' ||
      $colorName === 'inactive' ||
      $colorName === 'success'
        ? 'var(--button-bg)'
        : 'var(--white)'};
    --button-border: var(--button-bg);
    color: var(--button-text);
    border: 1px solid var(--button-border);
    background: transparent;
  }
`;

export const buttonBasicCss = css<ButtonCssProps>`
  ${props => colors[props.$colorName]}

  /* overwritten when bordered */
  border: none;
  border-radius: 999px;

  white-space: nowrap;

  ${props => (props.$isBordered ? borderedStyle : '')}

  position: relative;
  /* padding-top: $ {({ isBordered }) => (isBordered ? '11px' : '12px')};
  padding-bottom: $ {({ isBordered }) => (isBordered ? '11px' : '12px')}; */
  padding-left: ${({ $widePadding, $minimalPadding }) =>
    $minimalPadding ? '4px' : $widePadding ? '40px' : '15px'};
  padding-right: ${({ $widePadding, $minimalPadding }) =>
    $minimalPadding ? '4px' : $widePadding ? '40px' : '15px'};
  height: 40px;
  /* If displayed inside Flex container, make sure it retains standard size */
  flex: none;

  background-color: var(--button-bg);
  color: var(--button-text);

  outline: none;

  text-align: center;
  font-size: var(--small);
  font-weight: ${({ $fontNotBold }) => ($fontNotBold ? 'initial' : 'bold')};
  letter-spacing: 0.03rem;
  cursor: pointer;
  width: ${props => (props.$isInline ? 'initial' : '100%')};
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    opacity 0.4s ease;

  &:hover:not([disabled]) {
    background-color: var(--button-bg-hover);
    color: var(--button-text-hover);
  }
  /* Cause modal buttons autoFocus, we want focus less distracting than hover */
  &:focus-visible:not([disabled]) {
    box-shadow: 0 0 3px 3px #a2a2a244;
  }

  &[disabled] {
    cursor: not-allowed;
  }
`;

const ButtonElement = styled.button<ButtonCssProps>`
  ${buttonBasicCss}
`;

type ButtonProps = {
  children: ReactNode;
  disabled?: boolean;
  widePadding?: boolean;
  minimalPadding?: boolean;
  bordered?: boolean;

  onClick?: () => void; // React.MouseEventHandler<HTMLElement>;
  submit?: boolean;
  inline?: boolean;
  className?: string;
  color?: Colors;
  title?: string;
  fontNotBold?: boolean;

  autoFocus?: boolean;
};

function Button({
  onClick,
  children,
  widePadding = false,
  minimalPadding = false,
  inline = false,
  submit = false,
  bordered = false,
  disabled = false,
  color = 'active',
  autoFocus,
  title,
  className,
  fontNotBold,
}: ButtonProps) {
  const { activeChain } = useSelectorActiveChain();
  return (
    <ButtonElement
      className={className}
      $activeChain={activeChain}
      onClick={onClick}
      type={submit ? 'submit' : 'button'}
      disabled={disabled}
      $isInline={inline}
      $isBordered={!!bordered}
      $fontNotBold={!!fontNotBold}
      $colorName={color}
      $widePadding={widePadding}
      $minimalPadding={minimalPadding}
      title={title}
      autoFocus={autoFocus}
    >
      {children}
    </ButtonElement>
  );
}

export default Button;
